import { useState } from 'react';

const acceptedItems = [
  { label: 'デスクトップPC', note: 'メーカー・年式問わず' },
  { label: 'ノートパソコン', note: 'Windows / Mac 対応' },
  { label: 'タブレット端末', note: 'iPad・Android等' },
  { label: 'プリンター・複合機', note: '業務用・家庭用共に可' },
  { label: 'サーバー・NAS', note: '法人向け機器も対応' },
  { label: '周辺機器', note: 'モニター・キーボード等' },
];

const flowSteps = [
  { num: '1', title: 'お問い合わせ', desc: 'お電話またはフォームより機器の情報をお知らせください。' },
  { num: '2', title: '無料査定', desc: '引き取り可否と台数をご確認します。費用は一切かかりません。' },
  { num: '3', title: '日程調整', desc: '都合の良い日時にお伺いします。大和市・近隣エリアは最短即日対応。' },
  { num: '4', title: '安全データ消去', desc: '情報漏洩防止のため、専用ソフトで確実にデータを消去します。' },
  { num: '5', title: '完了証明書発行', desc: 'データ消去の証明書を発行。コンプライアンス対応もバッチリです。' },
];

const PCCollectionSection: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      {/* PC Collection Section */}
      <section id="pc-collection" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          {/* Badge + Heading */}
          <div className="text-center mb-14">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
              無料サービス
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 leading-tight mb-5">
              不要になったPCや機器、<br />
              <span className="text-emerald-600">無料で引き取ります</span>
            </h2>
            <p className="text-slate-500 text-base max-w-2xl mx-auto leading-relaxed">
              古いPC・サーバー・周辺機器の処分にお困りではありませんか？<br />
              株式会社湘南ビジネスサポートでは、神奈川県の企業様を中心に<br />
              不用機器を無料で引き取り、安全にデータ消去致します。
            </p>
          </div>

          {/* Main content grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-stretch mb-14">
            {/* Left: Image + Points */}
            <div className="flex flex-col gap-6">
              <div className="rounded-2xl overflow-hidden h-56">
                <img
                  src="https://public.readdy.ai/ai/img_res/fdfaa6e5b355e77842a3420abb7bd077.jpg"
                  alt="不要PC引き取りイメージ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-emerald-50 rounded-2xl p-6 flex flex-col gap-4">
                {[
                  { title: '引き取り費用は完全無料', desc: '出張費・処分費・データ消去費 すべて0円' },
                  { title: 'データ完全消去で安心', desc: '専用ソフトで確実に消去。証明書も発行します' },
                  { title: '神奈川県エリア出張対応', desc: '神奈川県、東京都町田市等' },
                ].map((pt) => (
                  <div key={pt.title}>
                    <p className="text-slate-800 font-bold text-sm">{pt.title}</p>
                    <p className="text-slate-500 text-xs mt-0.5 leading-relaxed">{pt.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Accepted items grid */}
            <div className="flex flex-col gap-5">
              <h3 className="text-lg font-bold text-slate-800">引き取り対象機器</h3>
              <div className="grid grid-cols-2 gap-3">
                {acceptedItems.map((item) => (
                  <div
                    key={item.label}
                    className="bg-slate-50 rounded-xl p-4 flex flex-col text-center gap-2.5 border border-slate-100"
                  >
                    <p className="text-slate-800 font-bold text-xs">{item.label}</p>
                    <p className="text-slate-400 text-xs mt-0.5">{item.note}</p>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-1">
                <p className="text-amber-800 text-xs leading-relaxed">
                  引き取り対象外の機器もございます。まずはお気軽にご相談ください。台数・種別によっては対応できない場合があります。
                </p>
              </div>

              <button
                onClick={() => setIsModalOpen(true)}
                className="whitespace-nowrap w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 rounded-xl text-base transition-all duration-200 cursor-pointer mt-auto"
              >
                PC引き取りの詳細を見る
              </button>
            </div>
          </div>

          {/* Flow steps */}
          <div className="bg-slate-50 rounded-2xl p-8">
            <h3 className="text-center text-lg font-bold text-slate-800 mb-8">ご利用の流れ</h3>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 relative">
              <div className="hidden sm:block absolute top-5 left-0 right-0 h-0.5 bg-emerald-100 z-0 mx-14" />
              {flowSteps.map((step) => (
                <div key={step.num} className="flex flex-col items-center text-center relative z-10">
                  <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 text-white rounded-full font-bold text-sm mb-3">
                    {step.num}
                  </div>
                  <p className="text-slate-800 font-bold text-xs mb-1.5">{step.title}</p>
                  <p className="text-slate-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setIsModalOpen(false); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-y-auto max-h-[90vh]">
            {/* Modal header */}
            <div className="bg-emerald-600 px-6 py-5 flex items-start justify-between">
              <div>
                <p className="text-emerald-200 text-xs font-bold tracking-widest uppercase mb-1">無料サービス</p>
                <h3 className="text-white text-xl font-bold leading-tight">PC・不用機器<br />無料引き取りサービス</h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="whitespace-nowrap text-white/70 hover:text-white transition-colors cursor-pointer flex-shrink-0 mt-1 text-2xl font-light leading-none px-2"
              >
                ×
              </button>
            </div>

            <div className="p-6 flex flex-col gap-6">
              {/* Company info */}
              <div className="bg-emerald-50 rounded-xl p-4 flex flex-col gap-3">
                <p className="text-emerald-700 text-xs font-bold">お問い合わせ先</p>
                <p className="text-slate-800 text-sm font-semibold">株式会社湘南ビジネスサポート</p>
                <a href="tel:09027740856" className="text-slate-700 text-sm font-semibold cursor-pointer hover:text-emerald-600 transition-colors">
                  090-2774-0856
                </a>
                <a href="mailto:info.shonanbiz.spt@gmail.com" className="text-slate-700 text-xs cursor-pointer hover:text-emerald-600 transition-colors break-all">
                  info.shonanbiz.spt@gmail.com
                </a>
              </div>

              {/* Features */}
              <div>
                <p className="text-slate-700 font-bold text-sm mb-3">サービスの特徴</p>
                <div className="flex flex-col gap-2.5">
                  {[
                    '引き取り・出張費・データ消去費 すべて完全無料',
                    'データ消去証明書を発行（コンプライアンス対応）',
                    '1台から複数台まで対応可能',
                    '大和市・座間市・綾瀬市・相模原市・町田市 エリア対応',
                    '廃棄処分で困っていた大量のPCもまとめて対応',
                    '個人情報・機密データの漏洩リスクを確実に排除',
                  ].map((feat) => (
                    <p key={feat} className="text-slate-600 text-sm leading-relaxed">・{feat}</p>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 text-xs leading-relaxed">
                  機器の状態・種別によっては対応できない場合があります。まずはお電話またはメールにてお気軽にご相談ください。
                </p>
              </div>

              {/* CTA buttons */}
              <div className="flex flex-col gap-3">
                <a
                  href="tel:09027740856"
                  className="whitespace-nowrap w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3.5 rounded-xl text-sm transition-all duration-200 cursor-pointer flex items-center justify-center"
                >
                  電話で相談する（090-2774-0856）
                </a>
                <a
                  href="mailto:info.shonanbiz.spt@gmail.com"
                  className="whitespace-nowrap w-full bg-white hover:bg-slate-50 text-slate-700 font-semibold py-3.5 rounded-xl text-sm border border-slate-200 transition-all duration-200 cursor-pointer flex items-center justify-center"
                >
                  メールで問い合わせる
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PCCollectionSection;
